import { useMemo } from "react"
import { buildBillStatusMap, classifyBillStatus, computeBillTotal, getChartColor, saturateForChart } from "@pma/utils/helpers"
import type { ChartConfig } from "@components/common/chart"
import { AnalyticsFilters } from "@pma/types"

export function useFinancialComputed({
  projects,
  allBoqs,
  allBills,
  billStatusFilter,
  filters,
  translate,
}: {
  projects: any[] | undefined
  allBoqs: any[] | undefined
  allBills: any[] | undefined
  billStatusFilter: string | null
  filters?: AnalyticsFilters
  translate: (key: string) => string
}) {
  const projectNameMap = useMemo(() => {
    const map: Record<number, string> = {}
    ;(projects || []).forEach((p: any) => {
      map[p.id] = p.title || p.name || `Project ${p.id}`
    })
    return map
  }, [projects])

  const billStatusMap = useMemo(
    () => buildBillStatusMap(allBills || [], translate),
    [allBills, translate],
  )

  const {
    totalBoqValue,
    totalBilled,
    remaining,
    boqCount,
    billCount,
    contractorCount,
    boqByProject,
    billStatusBreakdown,
    totalBillsUnfiltered,
  } = useMemo(() => {
    let boqValue = 0
    let billed = 0
    let bills = 0
    const byProject: Record<number, number> = {}
    const uniqueCompanyIds = new Set<number>()

    ;(allBoqs || []).forEach((boq: any) => {
      const sum = Number(boq.boq_sum) || 0
      boqValue += sum
      const pid = boq._projectId
      byProject[pid] = (byProject[pid] || 0) + sum
      if (boq.company_id) uniqueCompanyIds.add(boq.company_id)
      if (boq.contractor_company_id) uniqueCompanyIds.add(boq.contractor_company_id)
    })

    let dateScopedBills = allBills || []
    if (filters?.dateRange?.from) {
      dateScopedBills = dateScopedBills.filter(
        (bill: any) => new Date(bill?.created_at || bill?.date || 0) >= filters.dateRange.from!,
      )
    }
    if (filters?.dateRange?.to) {
      dateScopedBills = dateScopedBills.filter(
        (bill: any) => new Date(bill?.created_at || bill?.date || 0) <= filters.dateRange.to!,
      )
    }

    const allStatusCounts: Record<string, number> = {}
    dateScopedBills.forEach((bill: any) => {
      const status = classifyBillStatus(bill)
      allStatusCounts[status] = (allStatusCounts[status] || 0) + 1
    })

    dateScopedBills.forEach((bill: any) => {
      const status = classifyBillStatus(bill)
      if (billStatusFilter && status !== billStatusFilter) return
      bills++
      billed += computeBillTotal(bill, false)
    })

    const boqProjectData = Object.entries(byProject)
      .map(([pid, val], i) => ({
        projectId: Number(pid),
        name: projectNameMap[Number(pid)] || `Project ${pid}`,
        value: val,
        fill: getChartColor(i),
      }))
      .sort((a, b) => b.value - a.value)

    const billStatusData = Object.entries(allStatusCounts).map(([status, count]) => {
      const info = billStatusMap.get(status)
      return {
        title: info?.label || status,
        statusKey: status,
        count,
        fill: saturateForChart(info?.color || "#6b7280"),
      }
    })

    return {
      totalBoqValue: boqValue,
      totalBilled: billed,
      remaining: boqValue - billed,
      boqCount: (allBoqs || []).length,
      billCount: bills,
      contractorCount: uniqueCompanyIds.size,
      boqByProject: boqProjectData,
      billStatusBreakdown: billStatusData,
      totalBillsUnfiltered: (allBills || []).length,
    }
  }, [allBoqs, allBills, projectNameMap, billStatusFilter, billStatusMap, filters?.dateRange])

  const utilizationPct = totalBoqValue > 0 ? Math.round((totalBilled / totalBoqValue) * 100) : 0

  const allBillStatuses = useMemo(() => Array.from(billStatusMap.keys()), [billStatusMap])

  const boqNameMap = useMemo(() => {
    const map: Record<number, string> = {}
    ;(allBoqs || []).forEach((boq: any) => {
      map[boq.id] = boq.name || boq.title || `BOQ ${boq.id}`
    })
    return map
  }, [allBoqs])

  // Chart configs
  const boqProjectConfig: ChartConfig = useMemo(() => {
    const cfg: ChartConfig = { value: { label: translate("Value"), color: "hsl(var(--chart-1))" } }
    boqByProject.forEach((d) => {
      cfg[d.name] = { label: d.name, color: d.fill }
    })
    return cfg
  }, [boqByProject, translate])

  const billStatusConfig: ChartConfig = useMemo(() => {
    const cfg: ChartConfig = { count: { label: translate("Bills"), color: "hsl(var(--chart-1))" } }
    billStatusBreakdown.forEach((d) => {
      cfg[d.title] = { label: d.title, color: d.fill }
    })
    return cfg
  }, [billStatusBreakdown, translate])

  return {
    projectNameMap,
    billStatusMap,
    totalBoqValue,
    totalBilled,
    remaining,
    boqCount,
    billCount,
    contractorCount,
    boqByProject,
    billStatusBreakdown,
    totalBillsUnfiltered,
    utilizationPct,
    allBillStatuses,
    boqNameMap,
    boqProjectConfig,
    billStatusConfig,
  }
}
